
import React, { useMemo } from "react";
import { useTournament } from "@/contexts/TournamentContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { SharedResultsEntry } from "./shared/SharedResultsEntry";
import { AmericanoLeaderboard } from "./americano/AmericanoLeaderboard";
import { Trophy, ClipboardList, ArrowLeft } from "lucide-react";
import { AmericanoAdapter } from "@/adapters/tournament-adapters";

interface AmericanoTournamentProps {
  participants: any[];
  onBack?: () => void;
}

export const AmericanoTournament: React.FC<AmericanoTournamentProps> = ({ participants, onBack }) => {
  const { language } = useLanguage();
  const { 
    tournamentState, 
    resetTournament,
    showLeaderboard,
    setShowLeaderboard,
    goToPreviousRound,
    finishAmericanoRound
  } = useTournament();

  // Create adapter for shared component using useMemo to ensure it updates when tournament state changes
  const americanoAdapter = useMemo(() => 
    new AmericanoAdapter(tournamentState, finishAmericanoRound), 
    [tournamentState, finishAmericanoRound]
  );

  // Show back button when NOT showing leaderboard and tournament is active or finished
  const showBackButton = !showLeaderboard && (tournamentState.isRoundActive || tournamentState.isFinished);

  const handleBack = () => {
    // If we're in round 1, go back to front page
    if (tournamentState.currentRound === 1) {
      if (onBack) {
        onBack();
      } else {
        resetTournament();
      }
    } else {
      // If we're in round 2+, go to previous round
      if (goToPreviousRound) {
        goToPreviousRound();
      }
    }
  };

  return (
    <div className="mt-8 border-t pt-6">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div className="flex items-center gap-2">
            {showBackButton && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleBack}
                className="text-xs sm:text-sm"
              >
                <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline ml-1">
                  {language === 'no' ? 'Tilbake' : 'Back'}
                </span>
              </Button>
            )}
            <h2 className="font-bold text-lg sm:text-xl flex items-center gap-2">
              <Trophy className="text-primary flex-shrink-0" />
              <span className="truncate">
                {tournamentState.isFinished
                  ? (language === 'no' ? 'Americano Turnering Ferdig' : 'Americano Tournament Finished')
                  : (language === 'no' ? `Americano Runde ${tournamentState.currentRound}` : `Americano Round ${tournamentState.currentRound}`)
                }
              </span>
            </h2>
          </div>
          
          <div className="flex gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowLeaderboard(!showLeaderboard)}
              className="text-xs sm:text-sm min-w-0"
            >
              <ClipboardList className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
              <span className="whitespace-nowrap">
                {showLeaderboard 
                  ? (language === 'no' ? 'Skjul resultater' : 'Hide results')
                  : (language === 'no' ? 'Vis resultater' : 'Show results')}
              </span>
            </Button>
          </div>
        </div>
        
        {showLeaderboard ? (
          <AmericanoLeaderboard />
        ) : tournamentState.isRoundActive ? (
          <SharedResultsEntry 
            adapter={americanoAdapter}
            currentRound={tournamentState.currentRound}
            totalRounds={tournamentState.totalRounds}
          />
        ) : tournamentState.isFinished ? (
          <AmericanoLeaderboard />
        ) : (
          <AmericanoLeaderboard />
        )}
      </div>
    </div>
  );
};
